import React from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Divider,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import {
  ControversialContentConfig,
  ControversialCategory,
} from "../../../../redux/reducers/assessmentReducer";

interface ControversialContentEditorProps {
  config: ControversialContentConfig | null | undefined;
  onChange: (config: ControversialContentConfig | null) => void;
  isEditing: boolean;
}

const ControversialContentEditor: React.FC<ControversialContentEditorProps> = ({
  config,
  onChange,
  isEditing,
}) => {
  const [enabled, setEnabled] = React.useState(!!config);

  React.useEffect(() => {
    setEnabled(!!config);
  }, [config]);

  const handleToggleEnabled = (checked: boolean) => {
    setEnabled(checked);
    if (checked && !config) {
      // Initialize with default config
      onChange({
        controversial_categories: [],
        classification_context: "",
        require_confirmation: true,
        cooldown_timer: 3,
        response_mode: "non_committal",
      });
    } else if (!checked) {
      onChange(null);
    }
  };

  const handleConfigChange = (
    field: keyof ControversialContentConfig,
    value: any
  ) => {
    if (!config) return;
    onChange({
      ...config,
      [field]: value,
    });
  };

  const handleCategoryChange = (
    index: number,
    field: keyof ControversialCategory,
    value: any
  ) => {
    if (!config) return;
    const newCategories = [...config.controversial_categories];
    newCategories[index] = {
      ...newCategories[index],
      [field]: value,
    };
    onChange({
      ...config,
      controversial_categories: newCategories,
    });
  };

  const handleTermChange = (
    categoryIndex: number,
    termIndex: number,
    value: string
  ) => {
    if (!config) return;
    const newCategories = [...config.controversial_categories];
    const newTerms = [...newCategories[categoryIndex].associated_terms];
    newTerms[termIndex] = value;
    newCategories[categoryIndex] = {
      ...newCategories[categoryIndex],
      associated_terms: newTerms,
    };
    onChange({
      ...config,
      controversial_categories: newCategories,
    });
  };

  const addTerm = (categoryIndex: number) => {
    if (!config) return;
    const newCategories = [...config.controversial_categories];
    newCategories[categoryIndex] = {
      ...newCategories[categoryIndex],
      associated_terms: [...newCategories[categoryIndex].associated_terms, ""],
    };
    onChange({
      ...config,
      controversial_categories: newCategories,
    });
  };

  const removeTerm = (categoryIndex: number, termIndex: number) => {
    if (!config) return;
    const newCategories = [...config.controversial_categories];
    const newTerms = [...newCategories[categoryIndex].associated_terms];
    newTerms.splice(termIndex, 1);
    newCategories[categoryIndex] = {
      ...newCategories[categoryIndex],
      associated_terms: newTerms,
    };
    onChange({
      ...config,
      controversial_categories: newCategories,
    });
  };

  const addCategory = () => {
    if (!config) return;
    onChange({
      ...config,
      controversial_categories: [
        ...config.controversial_categories,
        {
          title: "",
          associated_terms: [],
          description: "",
          example: "",
        },
      ],
    });
  };

  const removeCategory = (index: number) => {
    if (!config) return;
    const newCategories = [...config.controversial_categories];
    newCategories.splice(index, 1);
    onChange({
      ...config,
      controversial_categories: newCategories,
    });
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6">Controversial Content Detection</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => handleToggleEnabled(e.target.checked)}
                disabled={!isEditing}
              />
            }
            label="Enable"
          />
        </Box>

        {enabled && config && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Configure detection of controversial content during assessments.
              When detected, the interviewer can shift to non-committal
              responses.
            </Typography>

            <TextField
              label="Classification Context"
              fullWidth
              value={config.classification_context}
              onChange={(e) =>
                handleConfigChange("classification_context", e.target.value)
              }
              disabled={!isEditing}
              placeholder="e.g., the project that {{human_name}} is requesting funding for"
              helperText="Template string describing what is being classified (use {{human_name}} for the interviewee)"
              sx={{ mb: 2 }}
            />

            <Box display="flex" gap={2} sx={{ mb: 2 }}>
              <FormControl fullWidth disabled={!isEditing}>
                <InputLabel>Response Mode</InputLabel>
                <Select
                  value={config.response_mode}
                  onChange={(e) =>
                    handleConfigChange(
                      "response_mode",
                      e.target.value as "no_change" | "non_committal"
                    )
                  }
                  label="Response Mode"
                >
                  <MenuItem value="no_change">No Change</MenuItem>
                  <MenuItem value="non_committal">Non-Committal</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Cooldown Timer (turns)"
                type="number"
                fullWidth
                value={config.cooldown_timer}
                onChange={(e) =>
                  handleConfigChange(
                    "cooldown_timer",
                    parseInt(e.target.value) || 3
                  )
                }
                disabled={!isEditing}
                helperText="Turns after detection before it expires"
                inputProps={{ min: 1 }}
              />
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={config.require_confirmation}
                  onChange={(e) =>
                    handleConfigChange("require_confirmation", e.target.checked)
                  }
                  disabled={!isEditing}
                />
              }
              label="Require Confirmation (two detections)"
              sx={{ mb: 3 }}
            />

            <Divider sx={{ my: 2 }} />

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="subtitle1">
                Controversial Categories
              </Typography>
              {isEditing && (
                <Button
                  startIcon={<AddIcon />}
                  onClick={addCategory}
                  size="small"
                  variant="outlined"
                >
                  Add Category
                </Button>
              )}
            </Box>

            {config.controversial_categories.length > 0 ? (
              <List>
                {config.controversial_categories.map((category, index) => (
                  <ListItem key={index} sx={{ display: "block", mb: 2, p: 0 }}>
                    <Card variant="outlined" sx={{ p: 2 }}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                      >
                        <Chip
                          label={`Category ${index + 1}`}
                          size="small"
                          color="primary"
                        />
                        {isEditing && (
                          <IconButton
                            size="small"
                            onClick={() => removeCategory(index)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>

                      <TextField
                        fullWidth
                        label="Title"
                        value={category.title}
                        onChange={(e) =>
                          handleCategoryChange(index, "title", e.target.value)
                        }
                        disabled={!isEditing}
                        sx={{ mb: 2 }}
                        placeholder="e.g., Surveillance or Tracking"
                      />

                      {/* Associated Terms Section */}
                      <Box sx={{ mb: 2 }}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={1}
                        >
                          <Typography variant="subtitle2">
                            Associated Terms ({category.associated_terms.length}
                            )
                          </Typography>
                          {isEditing && (
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => addTerm(index)}
                              variant="text"
                            >
                              Add Term
                            </Button>
                          )}
                        </Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mb: 1 }}
                        >
                          Keywords that indicate this category
                        </Typography>
                        {category.associated_terms.length > 0 ? (
                          <List dense>
                            {category.associated_terms.map(
                              (term, termIndex) => (
                                <ListItem
                                  key={termIndex}
                                  sx={{ px: 0, py: 0.5 }}
                                >
                                  <TextField
                                    fullWidth
                                    size="small"
                                    value={term}
                                    onChange={(e) =>
                                      handleTermChange(
                                        index,
                                        termIndex,
                                        e.target.value
                                      )
                                    }
                                    disabled={!isEditing}
                                    placeholder={`Term ${termIndex + 1}`}
                                  />
                                  {isEditing && (
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        removeTerm(index, termIndex)
                                      }
                                      sx={{ ml: 1 }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </ListItem>
                              )
                            )}
                          </List>
                        ) : (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontStyle: "italic", fontSize: "0.875rem" }}
                          >
                            No terms defined
                          </Typography>
                        )}
                      </Box>

                      <TextField
                        fullWidth
                        label="Description"
                        multiline
                        rows={2}
                        value={category.description}
                        onChange={(e) =>
                          handleCategoryChange(
                            index,
                            "description",
                            e.target.value
                          )
                        }
                        disabled={!isEditing}
                        sx={{ mb: 2 }}
                        placeholder="Describe what this category covers"
                      />

                      <TextField
                        fullWidth
                        label="Example"
                        multiline
                        rows={2}
                        value={category.example}
                        onChange={(e) =>
                          handleCategoryChange(index, "example", e.target.value)
                        }
                        disabled={!isEditing}
                        placeholder="Provide an example of content in this category"
                      />
                    </Card>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: "italic" }}
              >
                No controversial categories defined. Click "Add Category" to
                create one.
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ControversialContentEditor;
